import { Observable } from 'rxjs'
import { RowDetailEntry, RowDetailSource } from '../models/row-detail.model'

/**
 * Seam between the Position Detail sub-panel and the Flex Positions row-detail API, mirroring the
 * {@link FieldCatalogService} and {@link WorkbenchDataSource} seams. The panel binds
 * {@link HttpRowDetailService}, which hits the real backend — there is no fabricated row detail
 * anywhere in the running panel.
 *
 * Note the parameter type: {@link RowDetailSource} cannot express Clearwater, so the "no endpoint
 * for CW" rule is enforced by the type system here rather than by a runtime guard inside each
 * implementation. Callers resolve an origin through `rowDetailSourceOf` and handle its `null`.
 */
export abstract class RowDetailService {
  /**
   * Fetch every field the source holds for one row.
   *
   * A resolved empty array is a legitimate answer (the endpoint 200s with `data: []` for an unknown
   * row under a valid session), not an error. Implementations cache on `${source}|${rowId}` so
   * switching sub-panel tabs back to Position Detail is instant rather than a refetch.
   */
  abstract getRowDetail(
    source: RowDetailSource,
    rowId: string,
  ): Observable<RowDetailEntry[]>

  /**
   * Drop everything cached (called on account switch).
   *
   * Unlike {@link FieldCatalogService.invalidate} this takes no account id: the cache is scoped to
   * one panel instance and keyed on (source, rowId) only, so there is no per-account partition to
   * evict selectively — a row id from the previous account must simply never be served again.
   */
  abstract invalidate(): void
}
